import React, {
  useState,
  useLayoutEffect,
  useEffect,
  useMemo,
  useContext,
} from 'react';
import { Row, Col, Typography, Avatar, Rate, Button, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link, useHistory } from 'react-router-dom';
import { History } from 'history';
import { useTranslation } from 'react-i18next';
import { GoLocation, GoClock } from 'react-icons/go';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { FaRegDotCircle, FaStar, FaArrowLeft } from 'react-icons/fa';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { PageHeader } from '../layout/PageHeader';
import { Navbar, AuthModalContent } from '../layout/Header';
import { OrderButton } from '../layout/GlobalButton';
import { PlaceOrderHandler } from '../orders/PlaceOrder';
import { SkeletonLoading } from '../userprofile/components/Loading';
import Map from '../orders/components/Map';
import { MealDetailsCoverImage } from '../userprofile/MealDetails';
import { OrderDetails } from '../orders/OrderDetails';

import { getReviews } from '../../store/cooksprofile/actions';

import { getCooksProfileState } from '../../store/cooksprofile/selectors';
import { getAppGlobalState } from '../../store/app-globals/selectors';
import { getOrderState } from '../../store/orders/selector';
import { getCooks } from '../../store/orders/actions';
import { DateObjects } from '../../store/cooksprofile/types';
import moment from 'moment';
import { CenterProps } from '../dashboard/components/DesktopMealCarousel';
import { usePosition } from 'use-position';
import { calcCrow } from '../dashboard/components/DesktopMealCarousel';
import { getUserState } from '../../store/user/selector';
import { AccountTypeContext } from '../dashboard/Dashboard';

export interface MealDetailsProps {
  meal?: any;
  // onClick?: (e?: React.MouseEvent) => void;
}

export const Reviews: React.FC<MealDetailsProps> = ({ meal }) => {
  const { reviews } = useSelector(getCooksProfileState);
  const { isLoading } = useSelector(getAppGlobalState);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const getMealReviews = () => {
    dispatch(getReviews(meal));
  };

  useEffect(getMealReviews, [meal]);

  const NextArrow = ({ ...props }) => {
    const newprops = {
      className: props.className,
      currentslide: props.currentSlide,
      onClick: props.onClick,
      slidecount: props.slideCount,
    };
    return <MdKeyboardArrowRight color="#9c9c9c" size={25} {...newprops} />;
  };
  const PrevArrow = ({ ...props }) => {
    const newprops = {
      className: props.className,
      currentslide: props.currentSlide,
      onClick: props.onClick,
      slidecount: props.slideCount,
    };
    return <MdKeyboardArrowLeft color="#9c9c9c" size={25} {...newprops} />;
  };
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    // className: 'slides-container',
  };

  const getTime = (time: DateObjects) => {
    const newtime = moment(time).add(100, 'days');
    const currenttime = moment().format();
    return moment(newtime).isSame(currenttime)
      ? moment(newtime).calendar()
      : moment().format('ll');
  };

  return (
    <div className="slider-container">
      <Col className="user-review-container">
        <Row justify="space-between">
          <Typography.Text className="review-text">
            {t('User Reviews')}
          </Typography.Text>
        </Row>
      </Col>
      {isLoading ? (
        <SkeletonLoading />
      ) : reviews.length === 0 ? (
        <span className="review-text">{t('NO_REVIEWS')}</span>
      ) : (
        <Slider {...settings}>
          {reviews.map((item, i) => (
            <div key={i}>
              <Col>
                <div>
                  <Avatar src={item.image} size="large" />
                </div>
                <Typography.Text className="profile-text">
                  {item.firstName}
                </Typography.Text>
              </Col>
              <Col>
                <Typography.Text className="time-text">
                  {t('TODAY')}: {getTime(item.timeadded)}
                </Typography.Text>
                <div>
                  <Rate
                    character={<FaStar size={18} />}
                    // onChange={handleRateChange}
                    disabled
                    count={5}
                    value={item.rate}
                  />
                </div>
              </Col>
              <Col>
                <Typography.Text className="message-text">
                  {item.message}
                </Typography.Text>
              </Col>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export const MealCoverImage: React.FC<MealDetailsProps> = ({ meal }) => {
  const { userinfo } = useSelector(getOrderState);
  const { ratings, user } = useSelector(getUserState);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { forWho } = useContext(AccountTypeContext);
  const history: History = useHistory();
  const [cooksLocation, setCooksLocation] = useState<CenterProps>();
  const settings = useMemo(
    () => ({ enableHighAccuracy: true, timeout: 0, maximumAge: Infinity }),
    []
  );

  const watch = true;
  const { latitude, longitude } = usePosition(watch, settings);

  const cookLocation = () => {
    if (userinfo) {
      setCooksLocation({ ...userinfo?.location });
    }
  };

  const handleCancelPress = () => {
    Modal.destroyAll();
  };

  const handleLinkClick = (link: string) => (): void => {
    history.push(link);
    setTimeout(() => {
      Modal.destroyAll();
    }, 500);
  };

  const restrictionModalConfig = {
    icon: null,
    false: true,
    maskClosable: true,
    className: 'modal-content-container modal-content-description',
    okButtonProps: { style: { display: 'none' } },
    content: (
      <AuthModalContent
        onCreateAccountPress={handleLinkClick(
          `/authentication/registration/${forWho}`
        )}
        onLoginAccountPress={handleLinkClick(
          `/authentication/login/email/${forWho}`
        )}
        onPressCancel={handleCancelPress}
      />
    ),
  };

  useEffect(() => {
    dispatch(getCooks(meal.isGuest ? meal.cook : meal.guest));
  }, [dispatch, meal.isGuest, meal]);
  useEffect(cookLocation, [userinfo]);
  const onClickOrderButton = () => {
    if (user.userid) {
      PlaceOrderHandler({ meal, history });
      return;
    }
    Modal.info(restrictionModalConfig);
  };
  const goBack = () => {
    history.goBack();
  };
  const capitalize = (s: string) => {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const checklength = ratings.filter(item => item.user === userinfo.id);
  const rate = ratings
    .filter(v => v.user === userinfo.id)
    .reduce((a, b) => a + b.rate / checklength.length, 0);
  return (
    <div className="meal-cover-image">
      {meal.isOrder ? (
        <div>
          <Row
            justify="start"
            style={{ padding: '20px', alignItems: 'center' }}
          >
            <Button onClick={goBack}>
              <Row style={{ alignItems: 'center' }}>
                <FaArrowLeft size={15} color="#27aae1" />
                <Typography.Text
                  style={{ color: '#27aae1', marginLeft: '20px' }}
                >
                  {t('Back to my orders')}
                </Typography.Text>
              </Row>
            </Button>
          </Row>
          <div
            className="time-container"
            style={{
              backgroundColor:
                meal.status === 'accepted'
                  ? '#00A75A'
                  : meal.status === 'waiting'
                  ? '#eb983e'
                  : meal.status === 'refused'
                  ? '#9c9c9c'
                  : '#9c9c9c',
            }}
          >
            <Typography.Text
              className="detail-text"
              style={{
                color: '#ffffff',
              }}
            >
              {meal.status === 'waiting'
                ? t('Waiting for Accept')
                : meal.status === 'refused'
                ? t('Refused')
                : meal.isGuest
                ? meal.ArriveTime === undefined || meal.ArriveTime === ''
                  ? t('Ready')
                  : `${t('Ready in')} ${meal.ArriveTime}`
                : meal.ArriveTime === undefined || meal.ArriveTime === ''
                ? t('Ready')
                : `${t('Arrive in')} ${meal.ArriveTime}`}
            </Typography.Text>
          </div>
        </div>
      ) : null}
      <div
        style={{
          backgroundImage: `url(${meal.image})`,
        }}
        className="image-cover"
      />
      <div className="meal-details-content">
        <Row>
          <Col span={12} className="meal-details-container">
            <Typography.Text className="meal-title">
              {meal.title}
            </Typography.Text>
            <div style={{ marginTop: '20px' }}>
              <Typography.Text className="meal-details-description">
                {meal.description}
              </Typography.Text>
            </div>
          </Col>
          <Col span={12} className="meal-details-container">
            <Typography.Text className="price-text">
              {`Kč ${
                meal.mealprice === undefined ? meal.price : meal.mealprice
              }.00`}
            </Typography.Text>
            <div style={{ marginLeft: '5px' }}>
              <Typography.Text className="meal-details-description">
                {t('per item')}
              </Typography.Text>
            </div>
            <div className="orderbutton-container">
              <OrderButton onclick={onClickOrderButton} />
            </div>
            <div className="separator" />
            <Row
              style={{
                marginLeft: '5px',
                marginTop: '10px',
                alignItems: 'center',
              }}
            >
              <FaRegDotCircle size={15} />
              <Typography.Text
                className="meal-details-portions"
                style={{ marginLeft: '10px' }}
              >
                {`${meal.portions} ${t('Quantity')}`}
              </Typography.Text>
              <Typography.Text
                className="meal-details-description"
                style={{ marginLeft: '10px' }}
              >
                {t('Available')}
              </Typography.Text>
            </Row>
            <Row
              style={{
                marginLeft: '5px',
                marginTop: '10px',
                alignItems: 'center',
              }}
            >
              <GoLocation size={15} />
              <Typography.Text
                className="meal-details-description"
                style={{ marginLeft: '10px' }}
              >
                {calcCrow(
                  cooksLocation?.lat,
                  cooksLocation?.lng,
                  latitude,
                  longitude
                ).toFixed(2)}{' '}
                km
              </Typography.Text>
            </Row>
            <Row
              style={{
                marginLeft: '5px',
                marginTop: '10px',
                alignItems: 'center',
              }}
            >
              <GoClock size={15} />
              <Typography.Text
                className="meal-details-description"
                style={{ marginLeft: '10px' }}
              >
                {meal.time !== '' ? meal.time : t('CONSTANT_OFFER')}
              </Typography.Text>
            </Row>
            <div className="separator" />
            <Typography.Text className="meal-details-description">
              {t(`${capitalize(userinfo.type)}s Details`)}
            </Typography.Text>
            <Link
              to={{
                pathname: '/userprofile/profile',
                state: userinfo,
              }}
            >
              <Col className="userinfo-container">
                <Row justify="start">
                  <Avatar
                    src={userinfo.image}
                    size="small"
                    style={{ marginRight: '10px' }}
                  />
                  <Typography.Text className="profile-text" ellipsis>
                    {userinfo.firstName}
                  </Typography.Text>

                  <div style={{ position: 'absolute', right: 0 }}>
                    <FaStar
                      color="#F7C82E"
                      size={14}
                      style={{ marginRight: 5 }}
                    />
                    <Typography.Text className="rate-text">
                      {rate !== 0 ? rate : 'No Ratings'}
                    </Typography.Text>
                  </div>
                </Row>
                <Typography.Text className="address-text">
                  {`${
                    userinfo.address.city !== undefined
                      ? userinfo.address.city
                      : ''
                  }
                `}
                </Typography.Text>
                <Typography.Text className="address-note">
                  {userinfo.address.note !== undefined
                    ? userinfo.address.note
                    : ''}
                </Typography.Text>
                <Col>
                  <Typography.Text className="address-text">
                    {userinfo.address.street !== undefined
                      ? userinfo.address.street
                      : ''}
                  </Typography.Text>
                </Col>
              </Col>
            </Link>
          </Col>
        </Row>
        {meal.isOrder ? (
          <div>
            {/* <Button type="default" size="large" className="navigate-button">
              <Row justify="center">
                <TiLocationArrow size={22} color="#547ce6" />
                <Typography.Text className="navigate-text">
                  Navigate
                </Typography.Text>
              </Row>
            </Button> */}
            <Map order={meal} />
          </div>
        ) : (
          <div className="slider-container">
            <Reviews meal={meal} />
          </div>
        )}
      </div>
    </div>
  );
};

export const DesktopMealDetails: React.FC = () => {
  const location = useLocation();
  const { state } = location;
  const [width, setWidth] = useState<number>(0);
  useLayoutEffect(() => {
    const updateSize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  const isDesktop = width >= 768;
  return (
    <div className="profile">
      {isDesktop ? <Navbar /> : <PageHeader headerTitle="Meal Details" />}
      <Row justify="center" className="cooks-container">
        <Col xs={24} sm={24} md={24} lg={24} className="tab-header-container">
          {isDesktop ? (
            <MealCoverImage meal={state} />
          ) : (
            <MealDetailsCoverImage meal={state} />
          )}
        </Col>
      </Row>
    </div>
  );
};

export const DesktopOrderDetails: React.FC = () => {
  const location = useLocation();
  const { state } = location;
  const [width, setWidth] = useState<number>(0);
  useLayoutEffect(() => {
    const updateSize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  const isDesktop = width >= 768;
  return (
    <div className="profile">
      {isDesktop ? <Navbar /> : <PageHeader headerTitle="Order Details" />}
      <Row justify="center" className="cooks-container">
        <Col xs={24} sm={24} md={24} lg={24} className="tab-header-container">
          {isDesktop ? (
            <MealCoverImage meal={state} />
          ) : (
            <OrderDetails meal={state} />
          )}
        </Col>
      </Row>
    </div>
  );
};
